/* eslint-disable sort-imports, import/order, simple-import-sort/imports */
import { type SelectProps } from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDPFHoursCalculation from "src/generator/CustomInput/useDPFHoursCalculation";
import { useDynamicTextWidth } from "src/generator/hooks/useDynamicTextWidth";
import { useInputProps } from "src/generator/hooks/useInputProps";
import { useSuspendAutosave } from "src/generator/hooks/useSuspendAutosave";
import { getYachtId } from "src/utils";

import { usePostApi } from "../../../hooks";
import useTriggerInlineSave from "../../../hooks/useTriggerInlineSave";
import { useDispatch } from "../../../redux/hooks";
import { setEditing } from "../../../redux/slices/entrySlice";
import { setGuernseyType } from "../../../redux/slices/generatorFeaturesSlice";
import useAmbPrevData from "../../hooks/useAmbPrevData";
import useArbemaSelectValue from "../../CustomInput/useArbemaSelectValue";
import useEngineSelectData from "../../CustomInput/useEngineSelectData";
import useGetSelectData from "../../hooks/useGetSelectData";
import { useLogbookRefsContext } from "../../Logbook/LogbookRefsContext";
import { useShowContent } from "../../hooks/useShowContent";

import CustomSelectItem from "./CustomSelectItem";
import styles from "./CustomSelect.module.scss";
import { normalizeSelectValue } from "src/utils/mantineComboboxData";
import { mantineRenderOption } from "src/utils/mantineRenderOption";

interface ICustomSelect extends SelectProps {
  value: any;
  id: string;
  data: any[];
  required: boolean;
  onChange?: (value: string) => void;
  width?: string;
  withLabel?: boolean;
  insertedValue?: any;
  insertedIndexForValue?: number;
  sectionIndex?: number;
  tag?: string;
  rowIndex?: number;
  colIndex?: number;
  numberOfCols?: number;
  totalRows?: number;
  ambGetPreviousData?: string;
  allowText?: boolean;
  searchEntries?: string;
  onArbemaChange?: (value: string, colIndex: number, className: string) => void;
  feature?: string;
  unit?: string;
  withClass?: string;
  onKey?: string;
  textWidth?: string;
}

const CustomSelect = ({
  value,
  id,
  data,
  required,
  onChange,
  width,
  withLabel,
  insertedValue,
  insertedIndexForValue,
  sectionIndex,
  numberOfCols,
  totalRows,
  rowIndex,
  colIndex,
  tag,
  ambGetPreviousData,
  onArbemaChange,
  feature,
  unit,
  withClass,
  onKey,
  textWidth,
  disabled,
  ...rest
}: ICustomSelect) => {
  const dispatch = useDispatch();
  const triggerSave = useTriggerInlineSave(sectionIndex);
  const { post } = usePostApi();
  const { updateEngineSelect } = useEngineSelectData();
  const { handleShowContent } = useShowContent();
  const { lbeidOrPartId, lbid } = useParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  useSuspendAutosave(dropdownOpen);

  const { onChange: defaultOnChange, ...inputProps } = useInputProps(
    id,
    value,
    required,
  );

  const logbookRefsProps = useLogbookRefsContext(
    id,
    numberOfCols,
    rowIndex,
    totalRows,
  );

  const isSecStatus = unit === "secstatus";
  const inputRef = useRef<HTMLInputElement>(null);
  const isDynamicTextWidth = textWidth === "dynamic";

  const getData = useGetSelectData({ data, value }); // Use the Arbema hook if colIndex is provided
  const { handleSelectChange: handleArbemaChange, className } =
    useArbemaSelectValue({
      id,
      tag,
      colIndex: colIndex ?? 0,
      onSelectChange: onArbemaChange,
    });

  const { setEngineHeader } = useDPFHoursCalculation({
    id,
    tag,
    setValue: () => {},
  });

  const minSelectWidth = useMemo(
    () => (width ? Number(width) * 10 - 20 : 200),
    [width],
  );

  const getValue = useCallback(() => {
    const currentData = getData();

    if (inputProps.value != null && typeof inputProps.value !== "object") {
      const strVal = normalizeSelectValue(inputProps.value);
      if (!strVal) return null;
      const match = currentData.find(i => String(i.value) === strVal);
      return match ? String(match.value) : strVal;
    }

    // action index comming from sub in old format
    if (
      Boolean(insertedValue) &&
      currentData[Number(insertedValue)] &&
      !inputProps.value
    ) {
      return currentData[Number(insertedValue)].value;
    }
    // action index comming for parsing cells in colsToUUIDObject function
    if (
      insertedIndexForValue !== undefined &&
      !isNaN(insertedIndexForValue) &&
      !inputProps.value
    ) {
      if (!insertedIndexForValue) {
        return null;
      }
      return currentData[insertedIndexForValue].value;
    }

    if (!inputProps.value) return null;
    if (typeof inputProps.value === "string") return inputProps.value;
    const item = currentData.find(
      i =>
        i.chrisKey === inputProps.value.key &&
        i.label === inputProps.value.value,
    );
    if (item) return item.value;
    return null;
  }, [getData, insertedIndexForValue, insertedValue, inputProps.value]);

  const selectedValue = getValue();
  const selectData = useMemo(() => getData(), [getData]);
  const selectedLabel = useMemo(() => {
    const item = getData().find(i => i.value === selectedValue);
    return item?.label ?? selectedValue ?? "";
  }, [getData, selectedValue]);

  const {
    dynamicWidth: dynamicSelectWidth,
    sizerRef,
    updateDynamicWidth,
  } = useDynamicTextWidth({
    enabled: isDynamicTextWidth,
    value: selectedLabel,
    minWidth: minSelectWidth,
    buffer: 70,
    inputRef,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    logbookRefsProps.onKeyDown(e);
  };
  const handleChange = useCallback(
    (newValue: string | null) => {
      if (!newValue) return;
      const currentValue = getValue();
      const item = getData().find(i => i.value === newValue);
      defaultOnChange({
        key: item?.chrisKey ?? newValue,
        value: item?.label ?? newValue,
      });

      if (tag === "guernseyTypes" && rowIndex !== undefined) {
        dispatch(setGuernseyType({ rowIndex, key: newValue }));
      }

      // Handle engine select data updates
      if (tag === "engine" && colIndex !== undefined) {
        updateEngineSelect(colIndex, newValue);
      }

      if (tag === "ss-engine") {
        setEngineHeader();
      }

      // Handle Arbema-specific logic if applicable
      if (colIndex !== undefined) {
        handleArbemaChange(newValue);
      }

      // Handle showContent feature based on onKey
      if (feature === "showContent" && withClass && onKey) {
        // Show content only when the selected value matches onKey
        const shouldShow = item?.chrisKey === onKey || item?.value === onKey;
        handleShowContent(shouldShow, withClass);
      }

      if (isSecStatus && currentValue !== newValue) {
        const selectedLabel = item?.label ?? newValue;
        void post({
          request: "record-history-action",
          body: {
            params: {
              yachtid: getYachtId(),
              lbid,
              lbeid: lbeidOrPartId,
              action: `User has changed the security status to ${selectedLabel}`,
            },
          },
          shouldRunAfterAutosave: true,
        });
      }

      if (isDynamicTextWidth) {
        updateDynamicWidth(item?.label ?? newValue, inputRef.current);
      }

      if (onChange) onChange(newValue);
      triggerSave();
      dispatch(setEditing(true));
    },
    [
      getData,
      defaultOnChange,
      onChange,
      dispatch,
      tag,
      colIndex,
      handleArbemaChange,
      updateEngineSelect,
      feature,
      withClass,
      onKey,
      handleShowContent,
      isSecStatus,
      getValue,
      post,
      lbid,
      lbeidOrPartId,
      rowIndex,
      setEngineHeader,
      isDynamicTextWidth,
      triggerSave,
      updateDynamicWidth,
    ],
  );

  // Handle initial showContent state on component mount
  useEffect(() => {
    if (feature === "showContent" && withClass && onKey) {
      const currentValue = getValue();
      if (currentValue) {
        const item = getData().find(i => i.value === currentValue);
        if (item) {
          const shouldShow = item.chrisKey === onKey || item.value === onKey;
          handleShowContent(shouldShow, withClass);
        }
      } else {
        // If no value selected, hide the content
        handleShowContent(false, withClass);
      }
    }
  }, [feature, getData, getValue, handleShowContent, onKey, withClass]);

  // Populate Redux store with existing engine select value on mount
  useEffect(() => {
    if (tag === "engine" && colIndex !== undefined) {
      const currentValue = getValue();
      if (currentValue) {
        updateEngineSelect(colIndex, currentValue);
      }
    }
  }, [tag, colIndex, updateEngineSelect, getValue]); // Only run when these dependencies change

  useEffect(() => {
    if (!isDynamicTextWidth) return;
    const inputElement = document.getElementById(id) as HTMLInputElement | null;
    if (inputElement) {
      updateDynamicWidth(selectedLabel, inputElement);
    }
  }, [id, isDynamicTextWidth, selectedLabel, updateDynamicWidth]);

  useAmbPrevData({
    id,
    dataTag: ambGetPreviousData,
    onChangeInput: handleChange,
    value: getValue(),
  });
  const label = useMemo(
    () =>
      data.find((item: any) => item.value === inputProps.value)?.inputLabel ??
      "",
    [data, inputProps.value],
  );
  return (
    <div style={{ position: "relative" }}>
      {isDynamicTextWidth && (
        <span
          ref={sizerRef}
          aria-hidden
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "pre",
            pointerEvents: "none",
          }}
        />
      )}
      <Select
        label={withLabel ? label : null}
        miw={isDynamicTextWidth ? dynamicSelectWidth : minSelectWidth}
        data={selectData}
        comboboxProps={{ withinPortal: true, position: "top", zIndex: 1000 }}
        classNames={{ option: styles.selectOption }}
        disabled={disabled}
        {...inputProps}
        ref={el => {
          logbookRefsProps.ref(el);
        }}
        onKeyDown={handleKeyDown}
        onDropdownOpen={() => setDropdownOpen(true)}
        onDropdownClose={() => setDropdownOpen(false)}
        value={selectedValue ?? null}
        onChange={handleChange}
        renderOption={mantineRenderOption(CustomSelectItem)}
        className={className ?? undefined}
      />
    </div>
  );
};

export default CustomSelect;
